import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../ui/input';
import { Search, ShieldCheck, ShieldX, Ban, RotateCcw, Trash2, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useModalClose } from '../../hooks/useModalClose';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROLE_FILTERS = [
  { value: '', label: 'All' },
  { value: 'patient', label: 'Patients' },
  { value: 'clinician', label: 'Clinicians' },
  { value: 'agency', label: 'Agencies' },
  { value: 'admin', label: 'Admins' },
];

const roleColor = (r) => ({
  patient: 'bg-blue-100 text-blue-700',
  clinician: 'bg-emerald-100 text-emerald-700',
  agency: 'bg-violet-100 text-violet-700',
  admin: 'bg-slate-200 text-slate-700',
}[r] || 'bg-slate-100 text-slate-600');

export const AdminUsersPanel = () => {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState('');
  const [role, setRole] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const { backdropProps, cardProps } = useModalClose({
    open: !!toDelete,
    onClose: () => setToDelete(null),
  });

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { q, role, status },
      });
      setUsers(res.data || []);
    } catch (e) {
      toast.error('Failed to load users');
    } finally { setLoading(false); }
  }, [q, role, status, token]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const act = async (promise, id, okMsg) => {
    setBusyId(id);
    try { await promise; toast.success(okMsg); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setBusyId(null); }
  };

  const verify = (u) => act(axios.put(`${API}/admin/clinicians/${u.id}/verify`, {}, authHeader), u.id, 'Clinician verified');
  const reject = (u) => act(axios.put(`${API}/admin/clinicians/${u.id}/reject`, {}, authHeader), u.id, 'Verification rejected');
  const setStatusFor = (u, s) => act(axios.patch(`${API}/admin/users/${u.id}/status`, { status: s }, authHeader), u.id, s === 'suspended' ? 'User suspended' : 'User reactivated');
  const doDelete = async () => {
    const u = toDelete; setToDelete(null);
    await act(axios.delete(`${API}/admin/users/${u.id}`, authHeader), u.id, 'User deleted');
  };

  return (
    <div data-testid="admin-users-panel">
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" className="pl-9 h-11 rounded-xl" data-testid="admin-users-search" />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {ROLE_FILTERS.map((r) => (
            <button key={r.value} onClick={() => setRole(r.value)} data-testid={`admin-users-filter-${r.value || 'all'}`}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${role === r.value ? 'bg-cn-primary text-white' : 'bg-slate-100 dark:bg-cn-ink-700 text-slate-600 dark:text-slate-300'}`}>
              {r.label}
            </button>
          ))}
          <button onClick={() => setStatus(status === 'suspended' ? '' : 'suspended')} data-testid="admin-users-filter-suspended"
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${status === 'suspended' ? 'bg-red-500 text-white' : 'bg-slate-100 dark:bg-cn-ink-700 text-slate-600 dark:text-slate-300'}`}>
            Suspended
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cn-primary" /></div>
      ) : users.length === 0 ? (
        <div className="py-12 text-center text-slate-500 dark:text-slate-400" data-testid="admin-users-empty">No users match your filters.</div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">{users.length} user(s)</p>
          {users.map((u) => {
            const suspended = u.status === 'suspended';
            const busy = busyId === u.id;
            return (
              <div key={u.id} data-testid={`admin-user-${u.email}`} className={`flex items-center justify-between gap-3 p-3 rounded-xl border ${suspended ? 'border-red-200 bg-red-50/50 dark:bg-red-900/10' : 'border-slate-200 dark:border-cn-ink-700 bg-white dark:bg-cn-ink-800'}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-cn-soft flex items-center justify-center flex-shrink-0 font-bold text-cn-primary dark:text-cn-sage-300 text-sm">
                    {(u.first_name?.[0] || '') + (u.last_name?.[0] || '')}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-slate-900 dark:text-white truncate">{u.first_name} {u.last_name}</p>
                      <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${roleColor(u.role)}`}>{u.role}</span>
                      {suspended && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-red-500 text-white">Suspended</span>}
                      {u.mfa_enabled && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">2FA</span>}
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                      {u.email}{u.clinician ? ` · ${u.clinician.license_type || 'Clinician'} · ${u.clinician.license_verified ? 'Verified' : 'Unverified'}` : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {u.role === 'clinician' && (u.clinician?.license_verified ? (
                    <button onClick={() => reject(u)} disabled={busy} data-testid={`admin-reject-${u.email}`} title="Reject verification" className="p-2 rounded-lg text-red-600 hover:bg-red-50 disabled:opacity-50"><ShieldX className="w-4 h-4" /></button>
                  ) : (
                    <button onClick={() => verify(u)} disabled={busy} data-testid={`admin-verify-${u.email}`} title="Verify clinician" className="p-2 rounded-lg text-emerald-600 hover:bg-emerald-50 disabled:opacity-50"><ShieldCheck className="w-4 h-4" /></button>
                  ))}
                  {u.role !== 'admin' && (
                    <>
                      {suspended ? (
                        <button onClick={() => setStatusFor(u, 'active')} disabled={busy} data-testid={`admin-reactivate-${u.email}`} title="Reactivate" className="p-2 rounded-lg text-cn-primary hover:bg-cn-soft disabled:opacity-50"><RotateCcw className="w-4 h-4" /></button>
                      ) : (
                        <button onClick={() => setStatusFor(u, 'suspended')} disabled={busy} data-testid={`admin-suspend-${u.email}`} title="Suspend" className="p-2 rounded-lg text-amber-600 hover:bg-amber-50 disabled:opacity-50"><Ban className="w-4 h-4" /></button>
                      )}
                      <button onClick={() => setToDelete(u)} disabled={busy} data-testid={`admin-delete-${u.email}`} title="Delete" className="p-2 rounded-lg text-red-600 hover:bg-red-50 disabled:opacity-50">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {toDelete && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" {...backdropProps} data-testid="admin-delete-modal">
          <div className="bg-white dark:bg-cn-ink-800 rounded-2xl p-6 max-w-sm w-full" {...cardProps}>
            <div className="flex items-center gap-2 mb-2 text-red-600"><Trash2 className="w-5 h-5" /><h3 className="font-bold text-lg text-slate-900 dark:text-white">Delete user?</h3></div>
            <p className="text-sm text-slate-600 dark:text-slate-300 mb-5">This permanently removes <span className="font-semibold">{toDelete.first_name} {toDelete.last_name}</span> ({toDelete.email}) and their profile, offers, bookings and transactions. This cannot be undone.</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setToDelete(null)} data-testid="admin-delete-cancel" className="px-4 py-2 rounded-lg text-sm font-medium bg-slate-100 dark:bg-cn-ink-700 text-slate-700 dark:text-slate-200">Cancel</button>
              <button onClick={doDelete} data-testid="admin-delete-confirm" className="px-4 py-2 rounded-lg text-sm font-semibold bg-red-600 text-white hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersPanel;
